import { AppBar, Box, Snackbar, Toolbar } from "@mui/material";
import { GoogleOAuthProvider } from "@react-oauth/google";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import { Location, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { getUserProfile } from "../api/user";
import { BurgerMenu } from "../components/BurgerMenu";
import { MillSwitcher } from "../components/MillSwitcher";
import { Loader } from "../components/Loader";
import { Menu } from "../components/Menu";
import ProfileActions from "../components/ProfileAction";
import { SwitchLang } from "../components/SwitchLang";
import { useAppContext } from "../contexts/AppContext";
import { getUserRoles } from "../helper/TokenHelper";
import { isUnileverEmail } from "../helper/email";
import { clearErrorMessage, showErrorMessage } from "../helper/helper";
import { ProfileModel } from "../models/person/ProfileModel";
import { colorDarkBlue, colorLight } from "../rsc/colors/colors";
import { AdminInfo, LogState, MenuState, MessageType, NFMStore, logout, setIsLoggedIn, updateAdmin, updateToken, updateUserProfile } from "../stores/store";
import Explorer from "./Explorer/Explorer";
import { ConfirmAccount } from "./create-account/ConfirmAccount";
import { CreateAccount } from "./create-account/CreateAccount";
import Auth from "./login/Auth";
import { firebaseAuth } from './login/firebaseInit';
import { FooterPempem } from "../components/Footer";
import './styles/colors.scss';

export async function clearSiteData() {
  return new Promise<void>((resolve, reject) => {
    const request = indexedDB.open('firebaseLocalStorageDb');
    request.onsuccess = function (event) {
      const db = request.result;
      const tx = db.transaction(['firebaseLocalStorage'], 'readwrite');
      const store = tx.objectStore('firebaseLocalStorage');
      const clearRequest = store.clear();
      clearRequest.onsuccess = function () {
        db.close();
        resolve();
      };
      clearRequest.onerror = function (e) {
        db.close();
        reject(e);
      };
    };
    request.onerror = function (e) {
      reject(e);
    };
  });
}


export async function signOut() {
  try {
    await firebaseAuth().signOut()
    await clearSiteData()
    NFMStore.dispatch(logout())
  } catch (error) {
    console.log(error)
  }
}

function RoutesPempem({ client }) {

  const { setProfile, profile, millSelected, setMillSelected } = useAppContext()
  const menuState: MenuState = useSelector((state: any) => state.menu.value)

  const token = useSelector((state: any) => state.token.value)
  const isLoggedIn: LogState | null = useSelector((state: any) => state.isLoggedIn.value)
  const user: ProfileModel | null = useSelector((state: any) => state.userProfile.value)

  const [errorMessage, setErrorMessage] = useState<MessageType>(null)

  const isAdminRef = useRef<AdminInfo>({ dashboardEnable: false, hasMill: false })

  const currentPath = useRef<Location>()

  const { t } = useTranslation()

  const location = useLocation()

  let isEmailSendProcess = useRef(false)
  const disableTokenChange = useRef<boolean>(false)

  const navigate = useNavigate()

  NFMStore.subscribe(() => {
    setErrorMessage(NFMStore.getState().errorMessage.value)
  })

  useEffect(() => {
    addItTokenChangeListener()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (user) {
      NFMStore.dispatch(setIsLoggedIn({ LoggedIn: true, forceRedirectLogin: false }))
    } else if (user === null) {
      NFMStore.dispatch(setIsLoggedIn({ LoggedIn: false, forceRedirectLogin: false }))
    }
  }, [user])

  async function fetchInitUserData() {
    const userProfile: ProfileModel | undefined = await fetchAndSetProfile()
    setProfile(userProfile)
    setMillSelected(userProfile?.organisations?.at(0))

    const dashboardEnable = userProfile.hasConglomerate()
      || userProfile.isSaranaUser()
      || isUnileverEmail(userProfile.person?.email)
    const hasMill = userProfile.hasMill()
    const isUserMill = userProfile.roles?.includes('usermill')

    if (dashboardEnable || isUserMill) {
      const object = { dashboardEnable: dashboardEnable, hasMill: hasMill }
      isAdminRef.current = object

      NFMStore.dispatch(updateUserProfile(userProfile.toJson()))
    } else {
      logOut()
      NFMStore.dispatch(updateUserProfile(null))
      if (currentPath.current.pathname.split('/')[1] === 'explorer') {
        navigate('/')
      } else {
        navigate(currentPath.current)
      }
      showErrorMessage(t('incorrect_role'))
    }
  }

  useEffect(() => {
    if (token) {
      if (!disableTokenChange.current) {
        fetchInitUserData()
      }
    } else if (token === null) {
      const object = { dashboardEnable: false, hasMill: false }
      isAdminRef.current = object
      NFMStore.dispatch(updateAdmin({ ...object }))
      NFMStore.dispatch(updateUserProfile(null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const fetchAndSetProfile = async (): Promise<ProfileModel | undefined> => {
    const res = await client.query({
      query: getUserProfile,
      context: {
        headers: {
          "role": "usermill"
        }
      },
      onError: (e) => showErrorMessage(e)
    })
    const profile = res.data.profile.profile
    // The gateway does not populate auth_role yet, so fall back to the roles
    // carried by the token. Drop the fallback once auth_role is served.
    const authRole = profile.auth_role ?? getUserRoles().map(role => ({ role }))
    const p = ProfileModel.build({
      ...profile,
      auth_role: authRole,
      organisations: (profile.organisations ?? [])
        .filter(o => !o.deleted_at)
        .sort((o1, o2) => o1.name.localeCompare(o2.name))
    })
    return p
  }

  const addItTokenChangeListener = () => {
    // Clean up any existing listener first

    // Create new listener and store the unsubscribe function
    firebaseAuth().onIdTokenChanged(async user => {
      if (user && user.emailVerified && !isEmailSendProcess.current) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        const token = await user.getIdToken()
        NFMStore.dispatch(updateToken(token))
      } else {
        NFMStore.dispatch(updateToken(null))
      }
    })
  }

  useEffect(() => {
    if (isLoggedIn?.LoggedIn === true) {
      if (currentPath.current.pathname === "/") {
        if (isAdminRef.current.hasMill) {
          navigate("explorer/my_prices")
        } else {
          navigate("explorer/dashboard")
        }
      }
    } else if (isLoggedIn?.LoggedIn === false) {
      if (currentPath.current.pathname.split('/')[1] === 'explorer' || isLoggedIn.forceRedirectLogin === true) {
        navigate('/')
      } else {
        navigate(currentPath.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  useEffect(() => {
    currentPath.current = location
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])


  const disableAuthListener = () => {
    disableTokenChange.current = true
  }

  const enableAuthListener = () => {
    disableTokenChange.current = false
  }

  const logOut = () => {
    setProfile(undefined)
    signOut()
  }


  const ShowCorrectView = (isAdminRef: AdminInfo) => {
    if (isLoggedIn?.LoggedIn === true) {
      return (
        <Routes>
          <Route path='/' element={<Navigate to={isAdminRef.hasMill ? "explorer/my_prices" : "explorer/profile"} replace />} />
          <Route path='explorer/*' element={<Explorer logOut={logOut} isAdmin={isAdminRef} client={client} menuOpen={false} />} />
          <Route path='create-account' element={<CreateAccount />} />
          <Route path='confirm-account' element={
            <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
              <ConfirmAccount enableAuth={enableAuthListener} disableAuth={disableAuthListener} />
            </GoogleOAuthProvider>
          } />
        </Routes>
      )
    } else if (isLoggedIn?.LoggedIn === false) {
      return <Routes>
        <Route path='/' element={<Auth client={client} />} />
        <Route path='create-account' element={<CreateAccount />} />
        <Route path='confirm-account' element={
          <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
            <ConfirmAccount enableAuth={enableAuthListener} disableAuth={disableAuthListener} />
          </GoogleOAuthProvider>
        } />
      </Routes>
    } else {
      return <div>
        <Loader isLoading={true} />
      </div>
    }
  }

  const menuRef = useRef<HTMLDivElement>()

  return (
    <div id="route-layout" style={{ minHeight: "100vh" }} className="darkColor">
      <Box sx={{ display: 'flex', height: "calc(100% - 64px)" }}>
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: "#0F293C" }}>
          <Toolbar>
            <div className="pempem-flex-row-rev" style={{ width: '100%', gap: 20 }}>
              <BurgerMenu />
              <div className="pempem-flex-row gap5" style={{ overflowX: 'scroll', scrollBehavior: 'unset', msOverflowStyle: 'none', scrollbarWidth: 'none', flex: 1, alignItems: 'center' }}>
                {/* <p style={{ alignContent: 'center', width: 'auto', flexShrink: 0 }}>Select mill:</p> */}
                <MillSwitcher />
                {/* {
                  profile?.organisations?.map(o => <div className={`pempem-flex-row-center header-tab ${millSelected?.id === o.id && 'active'}`} style={{ flexShrink: 0 }} onClick={() => setMillSelected(o)}>
                    <p>{o.name}</p>
                  </div>
                  )
                } */}
              </div>
              {user ?
                <div style={{ display: "flex", flexShrink: 0, width: 'auto', justifyContent: "right", gap: 5 }} ref={menuRef}>
                  <div hidden={menuState.isMobile} className="lightColor">
                    <SwitchLang color={"white"}/>
                  </div>

                  <ProfileActions
                    refForMenu={menuRef.current}
                    goToProfile={() => navigate("/explorer/profile")}
                    logOut={logOut}
                    goToAdminPage={() => navigate("/explorer/admin")}
                    isAdmin={isAdminRef.current}
                    style={{ height: "100%", userSelect: "none", margin: "0px", top: "0px", display: "inline", cursor: "pointer" }} />
                </div>
                : <></>}
            </div>
          </Toolbar>
        </AppBar>
        {
          isLoggedIn?.LoggedIn && <Menu isAdminRef={isAdminRef.current} />
        }
        <div style={{ flexGrow: 1, minHeight: "calc(100vh - 64px)", height: 'auto' }}>
          <Toolbar />
          {ShowCorrectView(isAdminRef.current)}
          {isLoggedIn?.LoggedIn === false && <FooterPempem /> }
        </div>
      </Box>
      {
        errorMessage ?
          <Snackbar
            anchorOrigin={{
              vertical: "top",
              horizontal: "center"
            }}
            open={true}
            autoHideDuration={6000}
            onClose={() => { clearErrorMessage() }}
            message={errorMessage.message}
            sx={{
              '& .MuiSnackbarContent-root': {
                backgroundColor: errorMessage.color,
                fontWeight: "600"
              }
            }}
          /> : <></>
      }
    </div>
  );
}
export default RoutesPempem;